/**
 * Loading System — Enterprise Design System
 *
 * Provides multiple loading indicator components:
 *   - LoadingBar: Fixed top linear progress bar
 *   - CardSkeleton: Card-shaped skeleton placeholder
 *   - TableRowSkeleton: Horizontal row of text placeholders
 *   - PageHeaderSkeleton: Icon circle + text line placeholders
 *   - ListItemSkeleton: Avatar circle + text block
 *   - Spinner: Centered circular progress indicator
 *   - TableSkeleton: Multiple skeleton rows matching column count
 */

import React from 'react';
import { Box, LinearProgress, Skeleton, CircularProgress } from '@mui/material';

// ── LoadingBar ────────────────────────────────────────────────────────────────
/**
 * Fixed top linear progress bar with gradient styling.
 * Returns null when not loading.
 *
 * @param {{ loading: boolean }} props
 */
export function LoadingBar({ loading }) {
  if (!loading) return null;

  return (
    <Box
      role="status"
      aria-label="Loading content"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        height: '2px',
      }}
    >
      <LinearProgress
        sx={{
          height: '2px',
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(90deg, #1a56db, #7c3aed)',
          },
          backgroundColor: 'transparent',
        }}
      />
    </Box>
  );
}

// ── CardSkeleton ──────────────────────────────────────────────────────────────
/**
 * Rectangular skeleton matching card shape (12px border-radius, card padding).
 */
export function CardSkeleton() {
  return (
    <Box
      role="status"
      aria-label="Loading content"
      sx={{ p: '20px', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}
    >
      <Skeleton variant="text" width="60%" height={24} animation="pulse" sx={{ animationDuration: '1.5s' }} />
      <Skeleton variant="text" width="80%" height={16} animation="pulse" sx={{ animationDuration: '1.5s', mt: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={80} animation="pulse" sx={{ animationDuration: '1.5s', mt: 2, borderRadius: '8px' }} />
    </Box>
  );
}

// ── TableRowSkeleton ──────────────────────────────────────────────────────────
/**
 * Horizontal flex row of text-width placeholders.
 */
export function TableRowSkeleton() {
  return (
    <Box
      role="status"
      aria-label="Loading content"
      sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 2 }}
    >
      <Skeleton variant="text" width="20%" height={20} animation="pulse" sx={{ animationDuration: '1.5s' }} />
      <Skeleton variant="text" width="25%" height={20} animation="pulse" sx={{ animationDuration: '1.5s' }} />
      <Skeleton variant="text" width="30%" height={20} animation="pulse" sx={{ animationDuration: '1.5s' }} />
      <Skeleton variant="text" width="15%" height={20} animation="pulse" sx={{ animationDuration: '1.5s' }} />
    </Box>
  );
}

// ── PageHeaderSkeleton ────────────────────────────────────────────────────────
/**
 * Icon placeholder circle + two text lines.
 */
export function PageHeaderSkeleton() {
  return (
    <Box
      role="status"
      aria-label="Loading content"
      sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
    >
      <Skeleton variant="circular" width={40} height={40} animation="pulse" sx={{ animationDuration: '1.5s' }} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="40%" height={28} animation="pulse" sx={{ animationDuration: '1.5s' }} />
        <Skeleton variant="text" width="25%" height={18} animation="pulse" sx={{ animationDuration: '1.5s' }} />
      </Box>
    </Box>
  );
}

// ── ListItemSkeleton ──────────────────────────────────────────────────────────
/**
 * Avatar circle + text block.
 */
export function ListItemSkeleton() {
  return (
    <Box
      role="status"
      aria-label="Loading content"
      sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}
    >
      <Skeleton variant="circular" width={36} height={36} animation="pulse" sx={{ animationDuration: '1.5s' }} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="50%" height={20} animation="pulse" sx={{ animationDuration: '1.5s' }} />
        <Skeleton variant="text" width="35%" height={16} animation="pulse" sx={{ animationDuration: '1.5s' }} />
      </Box>
    </Box>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
const SPINNER_SIZES = {
  small: 20,
  medium: 32,
  large: 48,
};

/**
 * Centered CircularProgress spinner.
 *
 * @param {{ size?: 'small' | 'medium' | 'large' }} props
 */
export function Spinner({ size = 'medium' }) {
  const px = SPINNER_SIZES[size] || SPINNER_SIZES.medium;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <CircularProgress
        size={px}
        role="status"
        aria-label="Loading content"
      />
    </Box>
  );
}

// ── TableSkeleton ─────────────────────────────────────────────────────────────
/**
 * Renders skeleton rows matching column count.
 *
 * @param {{ columns: number, rows?: number }} props
 */
export function TableSkeleton({ columns, rows = 5 }) {
  return (
    <Box role="status" aria-label="Loading content">
      {Array.from({ length: rows }, (_, rowIdx) => (
        <Box
          key={rowIdx}
          sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 2 }}
        >
          {Array.from({ length: columns }, (_, colIdx) => (
            <Skeleton
              key={colIdx}
              variant="text"
              height={20}
              animation="pulse"
              sx={{ animationDuration: '1.5s', flex: 1 }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}
